import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

// Phase 3 (F6): adviser "Portfolios" surface — portfolio MARKETING content.
//
// Reads the active `portfolio_content` marketing rows (title/subtitle/description/risk
// profile). These are the same marketing descriptors the public portfolio pages surface;
// proprietary factsheets are a separate, Sam-gated decision (m31 R4) and are NOT read here.

interface AdviserPortfolio {
  id: string;
  portfolio_code: string;
  title: string;
  subtitle: string | null;
  description: string;
  risk_profile: string | null;
}

export default function AdviserPortfolios() {
  const [portfolios, setPortfolios] = useState<AdviserPortfolio[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("portfolio_content")
          .select("id, portfolio_code, title, subtitle, description, risk_profile")
          .eq("status", "active")
          .order("portfolio_code", { ascending: true });

        if (!cancelled) {
          if (error) {
            setPortfolios([]);
          } else {
            setPortfolios((data as AdviserPortfolio[]) || []);
          }
        }
      } catch {
        if (!cancelled) setPortfolios([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Portfolios</h2>
        <p className="text-muted-foreground">
          Our portfolio range and its investment approach.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading portfolios...</div>
      ) : portfolios.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No portfolio materials available yet.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {portfolios.map((p) => (
            <Card key={p.id}>
              <CardHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">{p.portfolio_code}</Badge>
                  {p.risk_profile && (
                    <Badge variant="secondary">{p.risk_profile}</Badge>
                  )}
                </div>
                <CardTitle className="text-lg">{p.title}</CardTitle>
                {p.subtitle && <CardDescription>{p.subtitle}</CardDescription>}
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-4">{p.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
